import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small panel of controls under the button that opened it: options that don't need to be on
 * screen all the time. Like Menu, it's in a portal so nothing clips it, it stays inside the
 * window, and it closes on Escape or a press outside (menus and pickers opened from it count as
 * inside). Focus moves into it once it's placed and back to the button when it closes.
 */
export function Popover({
  anchor,
  onClose,
  ariaLabel,
  align = 'start',
  children,
}: {
  /** The button that opened it, to place the panel under and give focus back to. */
  anchor: HTMLElement;
  onClose: () => void;
  ariaLabel: string;
  /** Line up with the button's left edge, or its right. */
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const a = anchor.getBoundingClientRect();
      const { width, height } = el.getBoundingClientRect();
      const pad = 8;
      let left = align === 'end' ? a.right - width : a.left;
      left = Math.max(pad, Math.min(left, window.innerWidth - pad - width));
      let top = a.bottom + 6;
      if (top + height > window.innerHeight - pad) top = Math.max(pad, a.top - 6 - height);
      setPos({ left, top });
    };
    place();
    const ro = new ResizeObserver(place);
    if (ref.current) ro.observe(ref.current);
    window.addEventListener('resize', place);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [anchor, align]);

  // Focus once visible: hidden elements can't take focus (see Menu).
  const placed = !!pos;
  useEffect(() => {
    if (placed) ref.current?.querySelector<HTMLElement>('button, input, select, [tabindex]:not([tabindex="-1"])')?.focus();
  }, [placed]);

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Element;
      if (ref.current?.contains(t) || anchor.contains(t) || t.closest?.('[data-menu], .picker-list')) return;
      close.current();
    };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, [anchor]);

  return createPortal(
    <div
      ref={ref}
      className="popover"
      role="dialog"
      aria-label={ariaLabel}
      style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden', left: 0, top: 0 }}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        close.current();
        anchor.focus();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** A button that opens a Popover, and the open state that goes with it. */
export function usePopover() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return {
    anchor,
    open: !!anchor,
    toggle: (e: { currentTarget: HTMLElement }) => {
      // Read the button now: React clears currentTarget once the event is handled, before the updater runs.
      const el = e.currentTarget;
      setAnchor((a) => (a ? null : el));
    },
    close: () => setAnchor(null),
  };
}
